// 添加 Mapbox GL JS 类型定义
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../delivering/components/MapboxMapView.module.css';
import { useMapBoxPageLoadTrack } from '../../../graphql/mutations/mapBoxTrackLog';

declare global {
    interface Window {
        mapboxgl: any;
    }
}

// 注意：在 Mapbox 控制台设置 API Key 的域名白名单，即使 key 泄露，别人也无法在非白名单域名下使用
const DEFAULT_MAPBOX_API_KEY = "pk.eyJ1IjoibGl4aWFvaHVpdGl0YSIsImEiOiJjbWNsc2J3aTcwZDkyMmtwdGxiZW95YWt6In0.qoTeK6P5iQ8Mq62KdImwBQ";

// 标记点属性接口
interface MarkerProperties {
    id: string;
    number?: number|String;
    title?: string;
    description?: string;
    type?: string;
    status?: string;
    color?: string;
}

interface MapMarker {
    position: {
        lat: number;
        lng: number;
    };
    properties: MarkerProperties;
}

interface MapboxDetailViewProps {
    stopNumber: number|String;
    address: string;
    longitude?: number;
    latitude?: number;
    clusterCount?: number; // 新增：用于展示聚合数量
    markerProperties?: Partial<MarkerProperties>; // 都设置为可选
    onCoordinatesReady?: (latitude: number, longitude: number) => void; // 当地理编码完成后回调
    pageMarker?: string; // 页面标记，用于地图加载日志跟踪
    mapStateKey?: string; // 可选：用于持久化地图缩放/中心点的 key（建议传 deliveryTaskOrderId）
}

// 默认地图配置
const DEFAULT_MAP_CONFIG = {
    center: {
        lat: 40.7128,
        lng: -74.0060
    },
    zoom: 15
};

// 创建集群标记元素
const createClusterMarker = (stopNumber: string, count: number, markerColor: string = 'black') => {
    const el = document.createElement('div');
    el.style.position = 'absolute';
    el.style.cursor = 'pointer';
    el.style.zIndex = '1000';
    el.style.width = '40px';
    el.style.height = '50px';
    el.style.transform = 'translate(-50%, -100%)';
    el.style.transformOrigin = '50% 100%';
    el.style.pointerEvents = 'auto';
    el.style.boxSizing = 'border-box';

    const countText = count.toString();
    const fontSize = countText.length > 2 ? '14px' : countText.length > 1 ? '15px' : '16px';
    const uniqueId = `cluster-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    el.innerHTML = `
        <svg width="40" height="50" viewBox="0 0 40 50" fill="none" xmlns="http://www.w3.org/2000/svg" style="position: absolute; top: 0; left: 0; display: block;">
            <defs>
                <filter id="clusterShadow-${uniqueId}" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
                    <feOffset dx="0" dy="2" result="offsetblur"/>
                    <feComponentTransfer>
                        <feFuncA type="linear" slope="0.4"/>
                    </feComponentTransfer>
                    <feMerge>
                        <feMergeNode/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
            </defs>
            <path 
                d="M 20 2.5 C 11.75 2.5 5 9.25 5 17.5 C 5 25.75 20 47.5 20 47.5 C 20 47.5 35 25.75 35 17.5 C 35 9.25 28.25 2.5 20 2.5 Z" 
                fill="${markerColor}"
                filter="url(#clusterShadow-${uniqueId})"
            />
            <path 
                d="M 20 2.5 C 11.75 2.5 5 9.25 5 17.5 C 5 25.75 20 47.5 20 47.5 C 20 47.5 35 25.75 35 17.5 C 35 9.25 28.25 2.5 20 2.5 Z" 
                fill="none"
                stroke="white"
                stroke-width="2"
            />
            <g transform="translate(20, 20)">
                <g transform="translate(0, 5)">
                    <path d="M -10 -2 L 0 5 L 10 -2" fill="none" stroke="white" stroke-width="1.5" opacity="1"/>
                </g>
                <g transform="translate(0, 0)">
                    <path d="M -10 -2 L 0 5 L 10 -2" fill="none" stroke="white" stroke-width="2" opacity="1"/>
                </g>
                <g transform="translate(0, -5)">
                    <path d="M 0 -4 L 6 0 L 0 4 L -6 0 Z" fill="none" stroke="white" stroke-width="2.5" opacity="1"/>
                    <path d="M 0 -4 L 3 -1 L 0 1 L -3 -1 Z" fill="none" stroke="white" stroke-width="1.5" opacity="0.6"/>
                </g>
            </g>
        </svg>
        <div style="
            position: absolute;
            top: -32px;
            left: 50%;
            transform: translate(-50%, 0);
            display: flex;
            flex-direction: column;
            align-items: center;
            user-select: none;
            pointer-events: none;
        ">
            <div style="
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${fontSize};
                color: #fff;
                font-weight: bold;
                line-height: 1;
                text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
                background-color: ${markerColor};
                border-radius: 4px;
                padding: 4px 12px;
                white-space: nowrap;
                width: 40px;
                box-sizing: border-box;
            ">
                ${count}
            </div>
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-top: -1px;">
                <path d="M 6 8 L 0 0 L 12 0 Z" fill="${markerColor}"/>
            </svg>
        </div>
    `;
    return el;
};

// 加载 Mapbox GL JS
const loadMapboxScript = (apiKey: string): Promise<any> => {
    const loadCSS = () => {
        const cssId = 'mapbox-gl-css';
        if (!document.getElementById(cssId)) {
            const link = document.createElement('link');
            link.id = cssId;
            link.rel = 'stylesheet';
            link.type = 'text/css';
            link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
            document.head.appendChild(link);
        }
    };

    const checkMapbox = () => {
        if (window.mapboxgl) return true;
        return false;
    };

    return new Promise((resolve, reject) => {
        if (checkMapbox()) {
            resolve(window.mapboxgl);
            return;
        }
        loadCSS();
        const scriptId = 'mapbox-gl-js';
        if (document.getElementById(scriptId)) {
            const checkInterval = setInterval(() => {
                if (checkMapbox()) {
                    clearInterval(checkInterval);
                    resolve(window.mapboxgl);
                }
            }, 100);
            return;
        }
        const script = document.createElement('script');
        script.id = scriptId;
        script.type = 'text/javascript';
        script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            const checkInterval = setInterval(() => {
                if (checkMapbox()) {
                    clearInterval(checkInterval);
                    resolve(window.mapboxgl);
                }
            }, 100);
        };
        script.onerror = (error) => {
            console.error('Failed to load Mapbox GL JS:', error);
            reject(new Error('Failed to load Mapbox GL JS'));
        };
        document.head.appendChild(script);
    });
};

// Mapbox GL JS 服务
const mapboxService = {
    initializeMap: async (container: HTMLElement, apiKey: string, initialAddress?: string, providedLng?: number, providedLat?: number) => {
        try {
            await loadMapboxScript(apiKey);
            window.mapboxgl.accessToken = apiKey;
            let initialCenter = DEFAULT_MAP_CONFIG.center;
            if (initialAddress || (providedLng !== undefined && providedLng !== null && providedLat !== undefined && providedLat !== null)) {
                try {
                    const position = await mapboxService.geocode(initialAddress || '', apiKey, providedLng, providedLat);
                    if (position) initialCenter = position;
                } catch (error) {
                    console.warn('Failed to get initial position:', error);
                }
            }
            const map = new window.mapboxgl.Map({
                container: container,
                style: 'mapbox://styles/mapbox/streets-v11',
                center: [initialCenter.lng, initialCenter.lat],
                zoom: DEFAULT_MAP_CONFIG.zoom
            });
            map.addControl(new window.mapboxgl.NavigationControl(), 'bottom-right');
            
            map.on('zoom', () => {
                const zoom = map.getZoom();
                const scale = Math.max(0.5, Math.min(zoom / 14, 2));
                document.querySelectorAll('.custom-map-pin').forEach(pin => {
                    (pin as HTMLElement).style.transform = `scale(${scale})`;
                    (pin as HTMLElement).style.transformOrigin = 'bottom center';
                });
                document.querySelectorAll('.custom-map-pin-number').forEach(numberSpan => {
                    (numberSpan as HTMLElement).style.display = zoom < 13 ? 'none' : 'block';
                });
            });

            return map;
        } catch (error) {
            console.error('Error initializing map:', error);
            throw error;
        }
    },

    geocode: async (address: string, apiKey: string, providedLng?: number, providedLat?: number) => {
        if (providedLng !== undefined && providedLng !== null && providedLat !== undefined && providedLat !== null) {
            return { lat: providedLat, lng: providedLng };
        }
        try {
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${apiKey}&limit=1`
            );
            const data = await response.json();
            if (data.features?.[0]?.center) {
                const [lng, lat] = data.features[0].center;
                return { lat, lng };
            }
            return null;
        } catch (error) {
            console.error('Geocoding error:', error);
            return null;
        }
    },

    updateMarker: (map: any, marker: MapMarker, currentMarkerRef: any, clusterCount: number = 1) => {
        if (currentMarkerRef.current) {
            currentMarkerRef.current.remove();
        }

        const { position, properties } = marker;
        let markerElement;

        if (clusterCount > 1) {
            markerElement = createClusterMarker(properties.number?.toString() || '', clusterCount, 'black');
        } else {
            markerElement = document.createElement('div');
            markerElement.innerHTML = `
                <div class="${styles['custom-map-pin']}">
                    <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 0C8 0 0 7 0 16C0 28 16 40 16 40C16 40 32 28 32 16C32 7 24 0 16 0Z" fill="black"/>
                    </svg>
                    <span class="${styles['custom-map-pin-number']}">
                        ${properties.number || ''}
                    </span>
                </div>
            `;
        }

        const mapboxMarker = new window.mapboxgl.Marker({
            element: markerElement,
            anchor: 'bottom'
        })
            .setLngLat([position.lng, position.lat])
            .addTo(map);

        currentMarkerRef.current = mapboxMarker;
    }
};

const MapboxDetailViewTemporary: React.FC<MapboxDetailViewProps> = ({ 
    stopNumber, 
    address, 
    longitude, 
    latitude, 
    clusterCount = 1,
    markerProperties = {}, 
    onCoordinatesReady, 
    pageMarker,
    mapStateKey
}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);

    useMapBoxPageLoadTrack(pageMarker);

    const [apiKey, setApiKey] = useState('');
    const [error, setError] = useState('');
    const [isError, setIsError] = useState(false);
    const mapRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const markerRef = useRef<any>(null);
    const hasSavedViewportRef = useRef(false);
    const mapStateStorageKey = useMemo(() => {
        if (!pageMarker) return '';
        const suffix = mapStateKey || String(stopNumber);
        return `mapbox_detail_state_${pageMarker}_${suffix}`;
    }, [pageMarker, mapStateKey, stopNumber]);
    const updateTimerRef = useRef<NodeJS.Timeout>();
    const lastUpdateRef = useRef<{
        address: string;
        apiKey: string;
        stopNumber: number|String;
        markerProperties: any;
        clusterCount: number;
    } | null>(null);
    console.log('DeliveringTemporaryPage');
    const updateLocation = useCallback(async () => {
        if (!mapRef.current || !address || !apiKey) return;

        if (updateTimerRef.current) {
            clearTimeout(updateTimerRef.current);
        }

        updateTimerRef.current = setTimeout(async () => {
            setIsLoading(true);
            setIsError(false);
            try {
                const position = await mapboxService.geocode(address, apiKey, longitude, latitude);
                if (position) {
                    mapboxService.updateMarker(mapRef.current, {
                        position,
                        properties: {
                            id: `stop-${stopNumber}`,
                            number: stopNumber,
                            title: markerProperties.title || `Stop ${stopNumber}`,
                            ...markerProperties
                        }
                    }, markerRef, clusterCount);
                    mapRef.current.setCenter([position.lng, position.lat]);
                    if (onCoordinatesReady) {
                        onCoordinatesReady(position.lat, position.lng);
                    }
                } else {
                    setError(t('map.errors.locationNotFound') as string);
                    setIsError(true);
                }
            } catch (err) {
                console.error('Location update failed:', err);
                setError(t('map.errors.geocodeFailed') as string);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        }, 5000);
    }, [address, apiKey, stopNumber, markerProperties, longitude, latitude, clusterCount, t]);

    useEffect(() => {
        if (!apiKey || !containerRef.current) return;

        const initMap = async () => {
            try {
                let savedViewport: { center: { lat: number; lng: number }; zoom: number } | null = null;
                if (mapStateStorageKey) {
                    const raw = sessionStorage.getItem(mapStateStorageKey);
                    if (raw) {
                        try {
                            savedViewport = JSON.parse(raw);
                            hasSavedViewportRef.current = true;
                        } catch (e) {
                            hasSavedViewportRef.current = false;
                        }
                    } else {
                        hasSavedViewportRef.current = false;
                    }
                }

                mapRef.current = await mapboxService.initializeMap(containerRef.current!, apiKey, address, longitude, latitude);
                if ((address && apiKey) || (longitude !== undefined && longitude !== null && latitude !== undefined && latitude !== null)) {
                    const position = await mapboxService.geocode(address, apiKey, longitude, latitude);
                    if (position) {
                        mapboxService.updateMarker(mapRef.current, {
                            position,
                            properties: {
                                id: `stop-${stopNumber}`,
                                number: stopNumber,
                                title: markerProperties.title || `Stop ${stopNumber}`,
                                ...markerProperties
                            }
                        }, markerRef, clusterCount);
                        lastUpdateRef.current = { address, apiKey, stopNumber, markerProperties, clusterCount };
                        if (onCoordinatesReady) {
                            onCoordinatesReady(position.lat, position.lng);
                        }

                        // 如果有保存的地图视角（中心点/缩放），恢复它；否则沿用默认/地理编码中心点
                        if (savedViewport?.center && typeof savedViewport.zoom === 'number') {
                            mapRef.current.jumpTo({
                                center: [savedViewport.center.lng, savedViewport.center.lat],
                                zoom: savedViewport.zoom
                            });
                        }
                    } else {
                        setError(t('map.errors.locationNotFound') as string);
                        setIsError(true);
                    }
                }

                // 监听视角变化并保存（用于返回时恢复）
                if (mapStateStorageKey && mapRef.current) {
                    const saveViewport = () => {
                        try {
                            const center = mapRef.current.getCenter();
                            const zoom = mapRef.current.getZoom();
                            sessionStorage.setItem(mapStateStorageKey, JSON.stringify({
                                center: { lat: center.lat, lng: center.lng },
                                zoom
                            }));
                        } catch (e) {
                            // ignore
                        }
                    };
                    mapRef.current.on('moveend', saveViewport);
                    mapRef.current.on('zoomend', saveViewport);
                    // 保存一次初始状态（避免用户不操作也能恢复）
                    saveViewport();

                    // 把 handler 挂到 ref 上，便于 cleanup 时 off
                    (mapRef.current as any).__saveViewportHandler = saveViewport;
                }
            } catch (err) {
                console.error('Map initialization failed:', err);
                setError(t('map.errors.initFailed') as string);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };

        initMap();

        return () => {
            // 移除监听
            if (mapRef.current && (mapRef.current as any).__saveViewportHandler) {
                const handler = (mapRef.current as any).__saveViewportHandler;
                try {
                    mapRef.current.off('moveend', handler);
                    mapRef.current.off('zoomend', handler);
                } catch (e) {
                    // ignore
                }
            }
            if (markerRef.current) markerRef.current.remove();
            if (mapRef.current) mapRef.current.remove();
        };
    }, [apiKey, t]);

    useEffect(() => {
        const currentValues = { address, apiKey, stopNumber, markerProperties, clusterCount };
        const lastValues = lastUpdateRef.current;

        const hasChanged = !lastValues ||
            lastValues.address !== address ||
            lastValues.apiKey !== apiKey ||
            lastValues.stopNumber !== stopNumber ||
            lastValues.clusterCount !== clusterCount ||
            JSON.stringify(lastValues.markerProperties) !== JSON.stringify(markerProperties);

        if (!hasChanged || lastValues === null) return;

        if (!mapRef.current || !address || !apiKey) return;

        if (updateTimerRef.current) {
            clearTimeout(updateTimerRef.current);
        }

        updateTimerRef.current = setTimeout(async () => {
            setIsLoading(true);
            setIsError(false);
            try {
                const position = await mapboxService.geocode(address, apiKey, longitude, latitude);
                if (position) {
                    mapboxService.updateMarker(mapRef.current, {
                        position,
                        properties: {
                            id: `stop-${stopNumber}`,
                            number: stopNumber,
                            title: markerProperties.title || `Stop ${stopNumber}`,
                            ...markerProperties
                        }
                    }, markerRef, clusterCount);
                    // 有保存视角时，不要强制把地图中心点拉回地理编码位置（否则无法保持缩放/中心点）
                    if (!hasSavedViewportRef.current) {
                        mapRef.current.setCenter([position.lng, position.lat]);
                    }
                    lastUpdateRef.current = currentValues;
                } else {
                    setError(t('map.errors.locationNotFound') as string);
                    setIsError(true);
                }
            } catch (err) {
                console.error('Location update failed:', err);
                setError(t('map.errors.geocodeFailed') as string);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        }, 5000);

        return () => {
            if (updateTimerRef.current) clearTimeout(updateTimerRef.current);
        };
    }, [address, apiKey, stopNumber, markerProperties, clusterCount, t]);

    useEffect(() => {
        setApiKey(DEFAULT_MAPBOX_API_KEY);
    }, [t]);

    return (
        <div className="relative h-full bg-gray-100">
            {!isError && <div ref={containerRef} className="w-full h-full" />}
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                    <div className="text-lg">{t('map.loading')}</div>
                </div>
            )}
            {isError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#f2f2f2] border border-gray-200 rounded-lg">
                    <div className="flex flex-col items-center space-y-4 px-6">
                        <div className="text-4xl mb-2">📍</div>
                        <div className="text-gray-600 text-lg font-medium text-center">
                            {t('map.errors.locationNotFound')}
                        </div>
                        <div className="text-gray-500 text-base text-center">
                            {t('map.errors.cannotLocate')}
                        </div>
                        <button
                            onClick={() => updateLocation()}
                            className="mt-4 px-6 py-2 bg-white border border-gray-300 rounded-full text-gray-600 text-sm font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200"
                        >
                            {t('map.retry')}
                        </button>
                    </div>
                </div>
            )}
            {error && !isError && (
                <div className="absolute top-4 left-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}
        </div>
    );
};

export default MapboxDetailViewTemporary;
